import { Component, OnInit, ViewChild } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute } from "@angular/router";
import {
    AddressDetailsAttributes,
    BaseAddress,
    BaseUser,
    UserDetailsAttributes,
    UserDetailStatus,
} from "@models/user.model";
import { SnackBarConfig } from "~/src/app/constants/snackbar.constants";
import { AuthService } from "~/src/app/services/auth/auth.service";
import { CheckoutService } from "~/src/app/services/checkout-page/checkout.service";
import { UserService } from "~/src/app/services/user/user.service";
import { AddressComponent } from "~/src/app/user-details/address/address.component";
import { ProfileDetailsComponent } from "~/src/app/user-details/profile-details/profile-details.component";
import { Location } from "@angular/common";
import { RouteTrackingService } from "~/src/app/services/route-tracking/route-tracking.service";

@Component({
    selector: "app-address-form",
    templateUrl: "./address-form.component.html",
    styleUrl: "./address-form.component.scss",
    standalone: false,
})
export class AddressFormComponent implements OnInit {
    public isUserExist: boolean = false;
    public profile!: UserDetailsAttributes;
    public address!: BaseAddress;
    public selectedAddress!: AddressDetailsAttributes;
    public title: string = "";
    public id!: number;
    @ViewChild(ProfileDetailsComponent) profileDetailRef: ProfileDetailsComponent | undefined;
    @ViewChild(AddressComponent) addressDetailRef: AddressComponent | undefined;

    constructor(
        private userService: UserService,
        private snackBar: MatSnackBar,
        private authService: AuthService,
        private checkoutService: CheckoutService,
        private route: ActivatedRoute,
        private location: Location,
        private routeTrackingService: RouteTrackingService,
    ) {}

    ngOnInit(): void {
        if (this.authService.isUserLoggedIn) {
            this.userService.isUserDetailsExist().subscribe((response) => {
                this.isUserExist = response;
            });
        } else {
            this.isUserExist = false;
        }

        this.route.params.subscribe((params) => {
            this.id = +params["id"];
            this.title = this.id ? "Edit your delivery address" : "Enter your delivery address";
        });
    }

    public receiveProfileData(profile: UserDetailsAttributes): void {
        this.profile = profile;
    }

    public receiveAddressData(address: BaseAddress): void {
        this.address = address;
    }

    public submitForm(): void {
        if (!this.id) {
            if (!this.authService.isUserLoggedIn) {
                // Guest user
                if (!this.isUserExist) {
                    // No profile yet
                    this.addUserDetailsInLocalStorage();
                }
            } else {
                // Logged in user
                if (!this.isUserExist) {
                    // No profile yet
                    this.addUserDetailsAndSelectNewAddress(this.profile, this.address);
                } else {
                    //  Profile already exists
                    this.addAddressAndSelectNewAddress(this.address);
                }
            }
        } else {
            this.userService.updateAddress(this.address).subscribe({
                next: (response) => {
                    this.openSnackBar(response, SnackBarConfig.Actions.OKAY);
                    this.routeTrackingService.redirectingPrevioulyUrl();
                },
                error: (error) => {
                    throw new Error(error.message);
                },
            });
        }
    }

    public isFormDataValid(): boolean {
        if (!this.isUserExist) {
            const profileFormValid =
                this.profileDetailRef?.profileForm.valid && this.profileDetailRef?.profileForm.dirty;
            const addressFormValid =
                this.addressDetailRef?.addressForm.valid && this.addressDetailRef?.addressForm.dirty;
            return !!(profileFormValid && addressFormValid);
        } else {
            const addressFormValid =
                this.addressDetailRef?.addressForm.valid && this.addressDetailRef?.addressForm.dirty;
            return !!addressFormValid;
        }
    }

    private addUserDetailsInLocalStorage(): void {
        this.checkoutService.setUserInLocalStorage(this.profile);
        this.checkoutService.setAddressInLocalStorage(this.address);
        this.routeTrackingService.redirectingPrevioulyUrl();
    }

    private addUserDetailsAndSelectNewAddress(profile: BaseUser, newAddress: BaseAddress): void {
        this.userService
            .addUserDetails({
                userDetails: profile,
                addressDetails: newAddress,
            })
            .subscribe((response) => {
                if (response) {
                    this.openSnackBar(UserDetailStatus.Add, SnackBarConfig.Actions.CLOSE);
                    this.routeTrackingService.redirectingPrevioulyUrl();
                }
            });
    }

    private addAddressAndSelectNewAddress(newAddress: BaseAddress): void {
        this.userService.addAddress(newAddress).subscribe((newAddress) => {
            if (newAddress) {
                this.openSnackBar(UserDetailStatus.Add, SnackBarConfig.Actions.CLOSE);
                this.routeTrackingService.redirectingPrevioulyUrl();
            }
        });
    }

    private openSnackBar(message: string, action: string): void {
        this.snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
            duration: SnackBarConfig.Duration.LONG,
        });
    }

    public goBack(): void {
        this.location.back();
    }
}
